import { useMemo } from 'react';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { convertRouteList, getRouteSegments } from '@lp/features/RoutePlanner/utils';
import { RouteSchema } from '@lp/features/RoutePlanner/components/RouteSchema';
import { AircraftLegResource, BaseLegResourceType, HubRouteLocation, RouteTypeEnum } from '@/api/apis/route';
import { CaseFacility, CaseResourceType } from '@/api/apis/case';

type MainRouteSchemaProps = {
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
};

export const MainRouteSchema = ({ donorHospital, txCenter }: MainRouteSchemaProps) => {
  const { organType } = useCaseDetailsWithReviews();
  const { Aircraft: aircrafts } = useRouteResources();
  const { list, getLocationById } = useRouteLocations();
  const { onOpenModal } = useRouteModalActions();
  const { legs: mainLegs, arrivalHub, departureHub } = useRouteDetails(RouteTypeEnum.Main);

  const aircraftsList = useMemo(
    () =>
      aircrafts?.map<BaseLegResourceType<AircraftLegResource>>((a) => ({
        id: a.resourceId,
        type: CaseResourceType.Aircraft,
        data: a.data,
      })) || [],
    [aircrafts],
  );

  const { segments, legs } = useMemo(() => {
    const defaultSegments = getRouteSegments({
      donorHospital,
      txCenter,
      legs: mainLegs,
      departureHub: getLocationById(departureHub?.locationId) as HubRouteLocation,
      arrivalHub: getLocationById(arrivalHub?.locationId) as HubRouteLocation,
    });

    return convertRouteList(defaultSegments, mainLegs);
  }, [arrivalHub, departureHub, donorHospital, getLocationById, mainLegs, txCenter]);

  const openRouteSchema = () => {
    onOpenModal({
      type: RouteModalTypes.RouteSchema,
      context: {
        route: {
          organType,
          segments,
          legs,
          locations: list,
          aircrafts: aircraftsList,
          routeType: 'full',
        },
      },
    });
  };

  return segments.length ? (
    <button className="flex flex-row items-center" aria-label="Main Route Schema" onClick={openRouteSchema}>
      <RouteSchema
        organType={organType}
        segments={segments}
        legs={legs}
        aircrafts={aircraftsList}
        locations={list}
        routeType="main"
        schemaType="alt"
      />
    </button>
  ) : null;
};
